import { isResource } from '@medplum/core'
import type {
  Coding,
  Extension,
  Organization,
  Reference,
  Schedule,
} from '@medplum/fhirtypes'
import { compact } from 'lodash-es'
import { SEEN_HEALTH_SYSTEM } from '#src/codeSystems.ts'
import { Tag } from '#src/decorators.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import { invariant } from '#src/invariant.ts'
import { SCHEDULE_SUBTYPE_SYSTEM } from '#src/models/schedules/dayCenterSchedule.ts'
import { resolveId } from '#src/references.ts'
import type { QueryTypes } from '#src/search.ts'

export const ORGANIZATION_OPERATING_WINDOWS_SUBTYPE =
  'organization-operating-windows'
export const OPERATING_WINDOW_EXTENSION = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/operating-window`
export const ATTENDANCE_SHIFT_EXTENSION = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/attendance-shift-block`

// A named recurring org window (booking shift, coverage window). `ical` is a
// full RFC 5545 snippet (DTSTART + RRULE + DURATION) — the same representation
// every recurring window in the scheduling engine uses.
export interface OperatingWindowDefinition {
  code: string
  label: string
  ical: string
}

// The clinic-local block a day-center attendance shift occupies. Kept
// structured (not an RRULE) because the weekly pattern comes from each
// participant's own attendance days — only the wall-clock block is org data.
export interface AttendanceShiftBlock {
  shift: string
  startTime: string
  durationMinutes: number
}

function stringValue(
  extension: Extension | undefined,
  url: string
): string | undefined {
  return extension?.extension?.find((entry) => entry.url === url)?.valueString
}

function integerValue(
  extension: Extension | undefined,
  url: string
): number | undefined {
  return extension?.extension?.find((entry) => entry.url === url)?.valueInteger
}

export const ORGANIZATION_SCHEDULE_ORG_SYSTEM = `${SEEN_HEALTH_SYSTEM}/organization-schedule-organization`

/**
 * An organization's operating calendar, modeled as a FHIR `Schedule` whose
 * `actor` is the Organization. (R4's `actor` target list omits Organization,
 * but Medplum enforces only cardinality — verified against dev — and the org
 * is the semantically honest actor for org-wide hours. The identifier carries
 * the same binding for deterministic search.) Holds the named booking windows
 * (AM/PM shifts) rules reference by code and the wall-clock blocks day-center
 * attendance shifts occupy. Ensured at init — the scheduling facts adapter
 * reads this instead of hardcoding wall-clock hours.
 */
export class OrganizationSchedule extends ExtendedResource<Schedule> {
  static readonly RESOURCE_TYPE = 'Schedule'

  static isInstance(value: unknown): value is Schedule {
    return (
      isResource(value) &&
      value.resourceType === 'Schedule' &&
      (value.meta?.tag ?? []).some(
        (tag) =>
          tag.system === SCHEDULE_SUBTYPE_SYSTEM &&
          tag.code === ORGANIZATION_OPERATING_WINDOWS_SUBTYPE
      )
    )
  }

  @Tag({ system: SCHEDULE_SUBTYPE_SYSTEM })
  _subtypeTag?: Coding

  constructor({ organization }: { organization: Reference<Organization> }) {
    const organizationId = resolveId(organization)
    invariant(organizationId, 'organization reference must carry an id')
    super({
      // A bare reference literal keeps this typed without casts: the fhirtypes
      // actor union excludes Organization, but an untyped { reference } object
      // is assignable to any Reference<T>.
      actor: [{ reference: `Organization/${organizationId}` }],
      active: true,
      identifier: [
        { system: ORGANIZATION_SCHEDULE_ORG_SYSTEM, value: organizationId },
      ],
    })
    this._subtypeTag = {
      system: SCHEDULE_SUBTYPE_SYSTEM,
      code: ORGANIZATION_OPERATING_WINDOWS_SUBTYPE,
    }
  }

  static queryByOrganization(
    organization: Reference<Organization>
  ): QueryTypes {
    return {
      identifier: `${ORGANIZATION_SCHEDULE_ORG_SYSTEM}|${resolveId(organization) ?? ''}`,
      _tag: `${SCHEDULE_SUBTYPE_SYSTEM}|${ORGANIZATION_OPERATING_WINDOWS_SUBTYPE}`,
    }
  }

  get organizationId(): string | undefined {
    return this.identifier(ORGANIZATION_SCHEDULE_ORG_SYSTEM)?.value
  }

  get operatingWindows(): OperatingWindowDefinition[] {
    return compact(
      (this.resource.extension ?? [])
        .filter((extension) => extension.url === OPERATING_WINDOW_EXTENSION)
        .map((extension) => {
          const code = stringValue(extension, 'code')
          const ical = stringValue(extension, 'ical')
          if (!code || !ical) {
            return undefined
          }
          return { code, label: stringValue(extension, 'label') ?? code, ical }
        })
    )
  }

  set operatingWindows(windows: OperatingWindowDefinition[]) {
    this.resource.extension = [
      ...(this.resource.extension ?? []).filter(
        (extension) => extension.url !== OPERATING_WINDOW_EXTENSION
      ),
      ...windows.map((window) => ({
        url: OPERATING_WINDOW_EXTENSION,
        extension: [
          { url: 'code', valueString: window.code },
          { url: 'label', valueString: window.label },
          { url: 'ical', valueString: window.ical },
        ],
      })),
    ]
  }

  get attendanceShiftBlocks(): AttendanceShiftBlock[] {
    return compact(
      (this.resource.extension ?? [])
        .filter((extension) => extension.url === ATTENDANCE_SHIFT_EXTENSION)
        .map((extension) => {
          const shift = stringValue(extension, 'shift')
          const startTime = stringValue(extension, 'startTime')
          const durationMinutes = integerValue(extension, 'durationMinutes')
          if (!shift || !startTime || !durationMinutes) {
            return undefined
          }
          return { shift, startTime, durationMinutes }
        })
    )
  }

  set attendanceShiftBlocks(blocks: AttendanceShiftBlock[]) {
    this.resource.extension = [
      ...(this.resource.extension ?? []).filter(
        (extension) => extension.url !== ATTENDANCE_SHIFT_EXTENSION
      ),
      ...blocks.map((block) => ({
        url: ATTENDANCE_SHIFT_EXTENSION,
        extension: [
          { url: 'shift', valueString: block.shift },
          { url: 'startTime', valueString: block.startTime },
          { url: 'durationMinutes', valueInteger: block.durationMinutes },
        ],
      })),
    ]
  }
}
